import { type ReactNode, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { Badge } from "@/components/ui/Badge";
import { DatePicker } from "@/components/ui/DatePicker";
import { usePageTitle } from "@/hooks/usePageTitle";
import { PageHeader, StatTile, SectionCard, Tabs } from "../components/ui";
import {
  useOversight, useAnalytics, useProjectCompare, useCoordinators, useMeta,
  useControlBoard, useSlaRules, useSlaMutation,
  useProcessTemplates, useProcessTemplateMutation, useTemplateTargets, useTemplateCalibration,
} from "./hooks";
import { useClients } from "@/features/clients/hooks/useClients";
import { useAuthStore } from "@/store/auth";
import { useTechnicalSubcontractors } from "../hooks";
import type { TechnicalSubcontractorRow } from "../api";
import { GlobalTodoTab } from "./TodoView";
import { TemplateGraphEditor } from "./TemplateGraphEditor";
import { MilestoneSequenceBoard } from "./MilestoneSequenceBoard";
import { LocationClocksCard } from "./LocationClocksCard";
import { fmtDate } from "../utils";
import type { CoordinatorRow, OversightRow, ProcessTemplate, ProcessTemplateStep, ProjectCompareCol, SlaRule, TemplateDiffRow, TemplateTarget } from "./types";
import {
  Dlc05Badge, InfoDot, HELP, SLA_SCOPE_PL, RESPONSIBLE_ROLE_PL, slaKeyPl,
} from "./labels";

type OTab = "overview" | "todo" | "control" | "analytics" | "compare" | "coordinators" | "subcontractors" | "meta" | "sla" | "templates";

const RAG: Record<string, string> = { red: "#dc2626", amber: "#d97706", green: "#16a34a" };

const PRIMARY = "var(--color-primary)";

function ChartBars({ data, color = PRIMARY, height = 180 }: { data: { name: string; value: number }[]; color?: string; height?: number }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 4, right: 8, bottom: 4, left: -18 }}>
        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
        <Tooltip />
        <Bar dataKey="value" fill={color} radius={[3, 3, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Przegląd ──────────────────────────────────────────────────────────────────
function OverviewTab() {
  const { data, isLoading } = useOversight();
  const [filter, setFilter] = useState<"all" | "urgent" | "stale" | "no_run">("all");
  if (isLoading) return <div className="text-sm text-[var(--color-text-muted)] mt-4">Ładowanie…</div>;
  const rows = (data ?? []).filter((r: OversightRow) =>
    filter === "urgent" ? r.open_urgent > 0 : filter === "stale" ? r.is_stale : filter === "no_run" ? r.no_fresh_run : true,
  );
  const totals = (data ?? []).reduce(
    (a, r) => ({ urgent: a.urgent + r.open_urgent, stale: a.stale + (r.is_stale ? 1 : 0), open: a.open + r.open_total }),
    { urgent: 0, stale: 0, open: 0 },
  );
  const worst = [...(data ?? [])].sort((a, b) => b.open_urgent - a.open_urgent || b.oldest_days - a.oldest_days)[0];
  return (
    <div className="mt-4 space-y-3">
      {/* jedno zdanie po ludzku zamiast samych liczb */}
      <div className="text-sm">
        {totals.open === 0 ? (
          <span><Badge color="green">czysto</Badge> Żaden projekt nie ma nieobsłużonych zaleceń.</span>
        ) : (
          <span>
            W portfelu czeka <b>{totals.open}</b> {totals.open === 1 ? "zalecenie" : "zaleceń"} do obsłużenia
            {totals.urgent > 0 && <>, w tym <b className="text-red-700">{totals.urgent} pilnych</b></>}
            {worst && worst.open_urgent > 0 && <> — najwięcej pali się w projekcie <b>{worst.name}</b></>}.
            {totals.stale > 0 && <> {totals.stale} {totals.stale === 1 ? "projekt jest zaniedbany" : "projekty są zaniedbane"} (nikt nie reaguje na zalecenia).</>}
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-2 text-xs font-mono">
        {(["all", "urgent", "stale", "no_run"] as const).map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-2 py-1 border ${filter === f ? "border-[var(--color-primary)] text-[var(--color-primary)]" : "border-[var(--color-border)] text-[var(--color-text-muted)]"}`}>
            {{ all: "Wszystkie", urgent: "Z pilnymi", stale: "Zaniedbane", no_run: "Bez analizy AI" }[f]}
          </button>
        ))}
      </div>
      <SectionCard title={`Projekty (${rows.length})`}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-[var(--color-text-muted)] border-b border-[var(--color-border)]">
                <th className="py-1.5 pr-2">Projekt</th><th className="pr-2">Prowadzi</th>
                <th className="pr-2">Główny problem<InfoDot text="Jedno zdanie od doradcy AI: co najbardziej blokuje ten projekt." /></th>
                <th className="pr-2">Do obsłużenia<InfoDot text="Ile zaleceń doradcy czeka na reakcję (czerwona liczba = w tym pilne)." /></th>
                <th className="pr-2">Najdłużej czeka<InfoDot text="Od ilu dni wisi najstarsze nieobsłużone zalecenie — im więcej, tym większe zaniedbanie." /></th>
                <th className="pr-2">Uwagi<InfoDot text="⚑ = zarząd zostawił ustalenia · „nikt nie reaguje” = zalecenia leżą · „bez analizy AI” = doradca jeszcze nie przeliczył projektu." /></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.connection_id} className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-elevated)]">
                  <td className="py-1.5 pr-2">
                    <Link className="hover:text-[var(--color-primary)]" to={`/power-projects/${r.project_id}?tab=advisor`}>{r.name}</Link>
                    {r.has_directive && <span title="Zarząd zostawił ustalenia dla tego projektu"> ⚑</span>}
                  </td>
                  <td className="pr-2 text-[var(--color-text-muted)]">{r.designer ?? "—"}</td>
                  <td className="pr-2 text-[var(--color-text-secondary)] max-w-[340px] truncate" title={r.executive_line}>{r.executive_line || "—"}</td>
                  <td className="pr-2">
                    <span className="font-mono">{r.open_total}</span>
                    {r.open_urgent > 0 && <span className="ml-1"><Badge color="red">{r.open_urgent} pilne</Badge></span>}
                  </td>
                  <td className="pr-2 font-mono">{r.oldest_days > 0 ? `${r.oldest_days} dni` : "—"}</td>
                  <td className="pr-2">
                    {r.is_stale && <Badge color="yellow">nikt nie reaguje</Badge>}
                    {r.no_fresh_run && <span className="ml-1"><Badge color="gray">bez analizy AI</Badge></span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}

// ── Analityka (recharts) ──────────────────────────────────────────────────────
function AnalyticsTab() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const params: Record<string, string> = {};
  if (from) params.from = from;
  if (to) params.to = to;
  const { data, isLoading } = useAnalytics(params);
  const ttc = data?.time_to_close_days;
  const aging = Object.entries(data?.backlog.aging ?? {}).map(([name, value]) => ({ name: `${name}d`, value }));
  const designers = (ttc?.by_designer ?? []).map((d) => ({ name: d.designer ?? "—", value: d.median ?? 0 }));
  return (
    <div className="mt-4 space-y-3">
      <DateRange {...{ from, to, setFrom, setTo }} />
      {isLoading ? <div className="text-sm text-[var(--color-text-muted)]">Ładowanie…</div> : (
        <>
          <div className="flex flex-wrap gap-2">
            <StatTile label={<>Backlog OPEN<InfoDot text="Nieobsłużone sugestie w wybranym okresie." /></>} value={data?.backlog.open_total ?? 0} />
            <StatTile label={<>Zgłoszone<InfoDot text="Ile nowych sugestii pojawiło się w okresie." /></>} value={data?.throughput.raised ?? 0} tone="blue" />
            <StatTile label={<>Zrobione<InfoDot text="Sugestie oznaczone jako zrealizowane (DONE) — liczone osobno od odrzuconych." /></>} value={data?.throughput.done ?? 0} tone="ok" />
            <StatTile label={<>Odrzucone<InfoDot text="Sugestie odrzucone (DISMISSED). Osobno od zrobionych, żeby odrzucanie nie udawało realizacji." /></>} value={data?.throughput.dismissed ?? 0} tone="warn" />
            <StatTile label={<>Mediana domknięcia [dni]<InfoDot text={HELP.resolveTime} /></>} value={ttc?.overall_median ?? "—"} />
          </div>
          <SectionCard title={<>Backlog wg wieku<InfoDot text={HELP.backlogAge} /></>}><ChartBars data={aging} /></SectionCard>
          <SectionCard title={<>Mediana czasu domknięcia — wg projektanta [dni]<InfoDot text="Dla każdego projektanta: medianowy czas od zgłoszenia do domknięcia sugestii. Wsparcie, nie ranking." /></>}><ChartBars data={designers} color="#2563eb" /></SectionCard>
        </>
      )}
    </div>
  );
}

// ── Porównanie projektów 1:1 ──────────────────────────────────────────────────
function ProgressCell({ done, total }: { done: number; total: number }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <span className="font-mono text-xs w-12 shrink-0 tabular-nums">{done}/{total}</span>
      <div className="h-1.5 w-full max-w-[180px] rounded-full bg-[var(--color-border)] overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${pct}%`, backgroundColor: pct >= 100 ? RAG.green : PRIMARY }}
        />
      </div>
      <span className="font-mono text-[10px] text-[var(--color-text-muted)] shrink-0">{pct}%</span>
    </div>
  );
}

function CompareTab() {
  const { data: rows } = useOversight();
  const opts = rows ?? [];
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const ids = [a, b].filter(Boolean);
  const { data } = useProjectCompare(ids);
  const cols = data?.projects ?? [];
  return (
    <div className="mt-4 space-y-3">
      <div className="flex flex-wrap gap-2 text-sm">
        {[[a, setA], [b, setB]].map(([val, set], i) => (
          <select key={i} className="input-base" value={val as string} onChange={(e) => (set as (v: string) => void)(e.target.value)}>
            <option value="">Projekt {i + 1}…</option>
            {opts.map((o) => <option key={o.connection_id} value={o.connection_id}>{o.name}</option>)}
          </select>
        ))}
      </div>
      {cols.length >= 2 && (
        <SectionCard title="Porównanie 1:1">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <tbody>
                {(
                  [
                    ["Projektant", (c) => c.designer ?? "—"],
                    ["Dni do terminu", (c) => c.days_to_deadline ?? "—"],
                    ["Działki OK", (c) => <ProgressCell done={c.progress.parcels_ok} total={c.progress.parcels_total} />],
                    ["Kamienie", (c) => <ProgressCell done={c.progress.milestones_done} total={c.progress.milestones_total} />],
                    ["Sugestie OPEN", (c) => c.open_total],
                    ["w tym pilne", (c) => c.open_by_priority?.urgent ?? 0],
                    ["Najstarsza [dni]", (c) => c.oldest_days],
                    ["Prognoza (neutr.) [dni]", (c) => c.forecast?.neutral?.days ?? "—"],
                    ["Bloker", (c) => c.top_bottleneck?.label ?? "—"],
                  ] as [string, (c: ProjectCompareCol) => ReactNode][]
                ).map(([label, fn]) => (
                  <tr key={label} className="border-b border-[var(--color-border)]">
                    <td className="py-1.5 pr-3 font-mono text-[11px] uppercase text-[var(--color-text-muted)] w-48">{label}</td>
                    {cols.map((c) => <td key={c.connection_id} className="py-1.5 pr-4">{fn(c)}</td>)}
                  </tr>
                ))}
                <tr>
                  <td className="py-1.5 pr-3 font-mono text-[11px] uppercase text-[var(--color-text-muted)]">Nazwa</td>
                  {cols.map((c) => <td key={c.connection_id} className="py-1.5 pr-4 font-semibold">{c.name}</td>)}
                </tr>
              </tbody>
            </table>
          </div>
        </SectionCard>
      )}
    </div>
  );
}

// ── Koordynatorzy — odbicia piłeczki ──────────────────────────────────────────
function CoordinatorsTab() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [ai, setAi] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const params: Record<string, string> = {};
  if (from) params.from = from;
  if (to) params.to = to;
  if (ai) params.ai = "1";
  const { data, isLoading } = useCoordinators(params);
  const opened: CoordinatorRow | undefined = (data?.coordinators ?? []).find((c) => c.designer_id === openId);
  return (
    <div className="mt-4 space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <DateRange {...{ from, to, setFrom, setTo }} />
        <label className="flex items-center gap-1 text-xs font-mono">
          <input type="checkbox" checked={ai} onChange={(e) => setAi(e.target.checked)} /> analiza AI
        </label>
      </div>
      {isLoading ? <div className="text-sm text-[var(--color-text-muted)]">Ładowanie…</div> : (
        <>
          <SectionCard title={`Co kto zrobił ${data?.window.from ?? ""} – ${data?.window.to ?? ""}`}>
            <p className="text-xs text-[var(--color-text-muted)] mb-2">
              „Odbicie piłeczki” = pchnięcie etapu do przodu (np. złożenie wniosku, domknięcie kroku).
              „Czas odbicia” = ile dni pozycja leżała po jego stronie, zanim ją ruszył. Kliknij wiersz,
              żeby zobaczyć koordynowane projekty z najpilniejszymi TODO oraz każde odbicie z osobna.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-[var(--color-text-muted)] border-b border-[var(--color-border)]">
                    <th className="py-1.5 pr-2">Osoba</th>
                    <th className="pr-2">Projekty<InfoDot text="Ile aktywnych projektów ZE koordynuje; czerwona liczba = w ilu z nich coś jest po terminie." /></th>
                    <th className="pr-2">Odbicia piłeczki<InfoDot text="Ile razy w okresie pchnął jakiś etap procesu do przodu (zmiana stanu w górę: złożone, gotowe…)." /></th>
                    <th className="pr-2">Mediana odbicia<InfoDot text="Typowy czas, przez jaki pozycja leżała po jego stronie zanim ją ruszył. Im mniej, tym szybciej „oddaje piłkę”." /></th>
                    <th className="pr-2">Zalecenia zrobione<InfoDot text="Ile zaleceń doradcy AI zrealizował (osobno od odrzuconych)." /></th>
                    <th className="pr-2">Odrzucone<InfoDot text="Ile zaleceń odrzucił — odrzucanie nie udaje realizacji." /></th>
                    <th className="pr-2">Po terminie<InfoDot text="Ile pozycji przekroczyło termin po jego stronie." /></th>
                    <th className="pr-2">Dostępność<InfoDot text="Dni robocze minus urlopy/L4 w okresie — do sprawiedliwego porównania (nieobecność nie karze)." /></th>
                    <th className="pr-2">Zrobione/dzień<InfoDot text="Zrealizowane zalecenia na dzień realnej dostępności." /></th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.coordinators ?? []).map((c) => (
                    <tr key={c.designer_id}
                      className={`border-b border-[var(--color-border)] cursor-pointer hover:bg-[var(--color-bg-elevated)] ${openId === c.designer_id ? "bg-[var(--color-bg-elevated)]" : ""}`}
                      onClick={() => setOpenId(openId === c.designer_id ? null : c.designer_id)}>
                      <td className="py-1.5 pr-2">{openId === c.designer_id ? "▾" : "▸"} {c.designer}</td>
                      <td className="pr-2 font-mono">
                        {(c.project_previews ?? []).length}
                        {(c.project_previews ?? []).filter((p) => p.overdue_total > 0).length > 0 && (
                          <span className="ml-1"><Badge color="red">{(c.project_previews ?? []).filter((p) => p.overdue_total > 0).length} opóźn.</Badge></span>
                        )}
                      </td>
                      <td className="pr-2 font-mono font-semibold">{c.returns_count ?? 0}</td>
                      <td className="pr-2 font-mono">{c.returns_median_days != null ? `${c.returns_median_days} dni` : "—"}</td>
                      <td className="pr-2 font-mono">{c.done}</td>
                      <td className="pr-2 font-mono">{c.dismissed}</td>
                      <td className="pr-2">{c.sla_breaches > 0 ? <Badge color="red">{c.sla_breaches}</Badge> : <span className="font-mono text-[var(--color-text-muted)]">0</span>}</td>
                      <td className="pr-2 font-mono">{c.available_days}/{c.working_days} dni</td>
                      <td className="pr-2 font-mono font-semibold">{c.done_per_available_day ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SectionCard>
          {opened && <CoordinatorProjectsPreview key={`proj-${opened.designer_id}`} coordinator={opened} />}
          {opened && <ReturnsDetail key={opened.designer_id} coordinator={opened} />}
          {data?.ai_analysis && (
            <SectionCard title="Analiza AI (sprawiedliwa, z uwzględnieniem dostępności)">
              <div className="text-sm whitespace-pre-wrap">{data.ai_analysis}</div>
            </SectionCard>
          )}
        </>
      )}
    </div>
  );
}

// ── Meta-skuteczność ──────────────────────────────────────────────────────────
function MetaTab() {
  const [ai, setAi] = useState(false);
  const { data, isLoading } = useMeta(ai ? { ai: "1" } : {});
  const cats = (data?.by_category ?? []).map((c) => ({ name: c.category, value: c.done }));
  return (
    <div className="mt-4 space-y-3">
      <label className="flex items-center gap-1 text-xs font-mono">
        <input type="checkbox" checked={ai} onChange={(e) => setAi(e.target.checked)} /> podsumowanie AI (meta-eng-aron)
      </label>
      {isLoading ? <div className="text-sm text-[var(--color-text-muted)]">Ładowanie…</div> : (
        <>
          <div className="flex flex-wrap gap-2">
            <StatTile label={<>Przebiegi OK<InfoDot text="Liczba udanych analiz AI (przebiegów doradcy) w portfelu." /></>} value={data?.runs_ok ?? 0} />
            <StatTile label={<>Akceptacja rad<InfoDot text="Odsetek rad przyjętych: zrobione / (zrobione + odrzucone). Mierzy trafność podpowiedzi AI." /></>} value={data?.acceptance_rate != null ? `${Math.round(data.acceptance_rate * 100)}%` : "—"} tone="ok" hint="zrobione / (zrobione+odrzucone)" />
            <StatTile label={<>% zweryfikowanych<InfoDot text={HELP.verifiedRate} /></>} value={data?.verified_rate != null ? `${Math.round(data.verified_rate * 100)}%` : "—"} tone={data && data.verified_rate != null && data.verified_rate < 0.5 ? "warn" : "ok"} hint="sygnał faktycznie zniknął" />
            <StatTile label={<>% z akcją<InfoDot text="Odsetek rad, dla których zastosowano proponowaną akcję jednym kliknięciem." /></>} value={data?.action_rate != null ? `${Math.round(data.action_rate * 100)}%` : "—"} tone="blue" />
            <StatTile label={<>Mediana domknięcia [dni]<InfoDot text={HELP.resolveTime} /></>} value={data?.median_time_to_close_days ?? "—"} />
          </div>
          <div className="flex flex-wrap gap-2">
            <StatTile label={<>Zweryfikowane<InfoDot text="Rady oznaczone jako zrobione, których sygnał faktycznie zniknął ze stanu projektu — realnie domknięte." /></>} value={data?.verified_done ?? 0} tone="ok" hint="potwierdzone realnie" />
            <StatTile label={<>Zadeklarowane<InfoDot text="Rady odklikane jako zrobione, ale sygnał wciąż jest w projekcie — brak potwierdzenia realizacji." /></>} value={data?.declared_done ?? 0} tone="warn" hint="klik bez potwierdzenia" />
            <StatTile label={<>Wróciły (reopened)<InfoDot text={HELP.reopened} /></>} value={data?.reopened ?? 0} tone={data && data.reopened > 0 ? "danger" : "default"} hint="sygnał wrócił po domknięciu" />
            <StatTile label="Zrobione / Odrzuc." value={`${data?.done ?? 0} / ${data?.dismissed ?? 0}`} />
          </div>
          {cats.length > 0 && <SectionCard title="Domknięte wg kategorii"><ChartBars data={cats} color="#16a34a" /></SectionCard>}
          {data?.ai_analysis && (
            <SectionCard title="Meta-eng-aron — ocena wartości doradcy">
              <div className="text-sm whitespace-pre-wrap">{data.ai_analysis}</div>
            </SectionCard>
          )}
        </>
      )}
    </div>
  );
}

/** Szybki podgląd koordynatora: jego projekty + najpilniejsze TODO i opóźnienia. */
function CoordinatorProjectsPreview({ coordinator }: { coordinator: CoordinatorRow }) {
  const previews = coordinator.project_previews ?? [];
  return (
    <SectionCard title={`Projekty koordynowane — ${coordinator.designer} (${previews.length})`}>
      {previews.length === 0 ? (
        <div className="text-sm text-[var(--color-text-muted)]">
          Ta osoba nie koordynuje żadnego aktywnego projektu ZE.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-[var(--color-text-muted)] border-b border-[var(--color-border)]">
                <th className="py-1.5 pr-2">Projekt</th>
                <th className="pr-2">Najpilniejsze TODO<InfoDot text="Do trzech najpilniejszych pozycji z zegarów procesu tego projektu — od najbardziej palącej." /></th>
                <th className="pr-2">Opóźnienie<InfoDot text="Ile dni po terminie jest najgorsza pozycja projektu. „—” = nic nie jest po terminie." /></th>
              </tr>
            </thead>
            <tbody>
              {previews.map((p) => (
                <tr key={p.connection_id} className="border-b border-[var(--color-border)] align-top">
                  <td className="py-1.5 pr-2 max-w-[240px]">
                    <Link className="hover:text-[var(--color-primary)]" to={`/power-projects/${p.project_id}?tab=todo`}>{p.project_name}</Link>
                    <div className="text-[10px] font-mono text-[var(--color-text-muted)]">
                      {p.todo_total} {p.todo_total === 1 ? "pozycja" : "pozycji"}
                      {p.overdue_total > 0 && <> · <span className="text-red-700">{p.overdue_total} po terminie</span></>}
                    </div>
                  </td>
                  <td className="py-1.5 pr-2">
                    {p.top_todos.length === 0 ? (
                      <span className="text-[var(--color-text-muted)]">Brak otwartych pozycji.</span>
                    ) : (
                      <ul className="space-y-0.5">
                        {p.top_todos.map((t, i) => (
                          <li key={i} className="flex flex-wrap items-baseline gap-x-2">
                            <b>{t.label}</b>
                            {t.phase_label && <span className="text-[var(--color-text-secondary)]">{t.phase_label}</span>}
                            {t.overdue_days > 0 ? (
                              <Badge color="red">po terminie o {t.overdue_days} dn.</Badge>
                            ) : t.days_left != null ? (
                              <span className="text-[10px] font-mono text-[var(--color-text-muted)] whitespace-nowrap">
                                zostało {t.days_left} dn.{t.due_date ? ` · do ${fmtDate(t.due_date)}` : ""}
                              </span>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                  <td className="py-1.5 pr-2 whitespace-nowrap">
                    {p.max_overdue_days > 0
                      ? <Badge color="red">{p.max_overdue_days} dn.</Badge>
                      : <span className="font-mono text-[var(--color-text-muted)]">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}

/** Szczegóły odbić jednej osoby: sortowanie po kolumnach + filtry (projekt / szukaj / tylko wolne). */
function ReturnsDetail({ coordinator }: { coordinator: CoordinatorRow }) {
  const returns = coordinator.returns ?? [];
  const [sortBy, setSortBy] = useState<"at" | "days" | "project_name">("at");
  const [dir, setDir] = useState<1 | -1>(-1); // najnowsze / największe najpierw
  const [project, setProject] = useState("");
  const [minDays, setMinDays] = useState("");
  const [q, setQ] = useState("");

  const projectOptions = useMemo(
    () => [...new Set(returns.map((r) => r.project_name))].sort(),
    [returns],
  );
  const filtered = returns
    .filter((r) =>
      (!project || r.project_name === project)
      && (!minDays || r.days >= Number(minDays))
      && (!q || `${r.item} ${r.action} ${r.detail ?? ""} ${r.project_name}`.toLowerCase().includes(q.toLowerCase())))
    .sort((a, b) => {
      const va = a[sortBy]; const vb = b[sortBy];
      return (va < vb ? -1 : va > vb ? 1 : 0) * dir;
    });

  const sortHeader = (key: typeof sortBy, label: string, help?: string) => (
    <th className="pr-2 cursor-pointer select-none whitespace-nowrap"
      onClick={() => { if (sortBy === key) setDir((d) => (d === 1 ? -1 : 1)); else { setSortBy(key); setDir(key === "at" || key === "days" ? -1 : 1); } }}>
      {label}{sortBy === key ? (dir === 1 ? " ▲" : " ▼") : ""}
      {help && <InfoDot text={help} />}
    </th>
  );

  return (
    <SectionCard title={`Odbicia piłeczki — ${coordinator.designer} (${coordinator.returns_count})`}>
      {returns.length === 0 ? (
        <div className="text-sm text-[var(--color-text-muted)]">
          Brak odbić w wybranym okresie — ta osoba nie pchnęła żadnego etapu do przodu.
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-1.5 text-xs">
            <select className="input-base text-xs" value={project} onChange={(e) => setProject(e.target.value)}>
              <option value="">Wszystkie projekty</option>
              {projectOptions.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <label className="inline-flex items-center gap-1">
              leżało ≥
              <input type="number" min={0} className="input-base w-16 text-xs" value={minDays}
                onChange={(e) => setMinDays(e.target.value)} />
              dni
            </label>
            <input className="input-base w-52 text-xs" placeholder="Szukaj…" value={q} onChange={(e) => setQ(e.target.value)} />
            {filtered.length !== returns.length && (
              <span className="text-[10px] font-mono text-[var(--color-text-muted)]">{filtered.length} z {returns.length}</span>
            )}
            <span className="text-[10px] font-mono text-[var(--color-text-muted)]">klik w nagłówek = sortowanie</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-[var(--color-text-muted)] border-b border-[var(--color-border)]">
                  {sortHeader("at", "Kiedy")}
                  {sortHeader("project_name", "Projekt")}
                  <th className="pr-2">Pozycja</th>
                  <th className="pr-2">Co zrobił</th>
                  <th className="pr-2">Notatka</th>
                  {sortHeader("days", "Leżało u niego", "Ile dni pozycja czekała od poprzedniego zdarzenia do jego ruchu — czas „odbicia piłeczki”.")}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr key={i} className="border-b border-[var(--color-border)]">
                    <td className="py-1.5 pr-2 font-mono whitespace-nowrap">{fmtDate(r.at)}</td>
                    <td className="pr-2 max-w-[260px]">
                      <Link className="hover:text-[var(--color-primary)]" to={`/power-projects/${r.project_id}?tab=todo`}>{r.project_name}</Link>
                    </td>
                    <td className="pr-2"><b>{r.item}</b></td>
                    <td className="pr-2 text-[var(--color-text-secondary)] whitespace-nowrap">{r.action}</td>
                    <td className="pr-2 text-[var(--color-text-muted)] max-w-[220px] truncate" title={r.detail || undefined}>{r.detail || "—"}</td>
                    <td className="pr-2 font-mono whitespace-nowrap">{r.days >= 7 ? <Badge color={r.days >= 14 ? "red" : "yellow"}>{r.days} dni</Badge> : `${r.days} dni`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && <div className="text-sm text-[var(--color-text-muted)] mt-2">Brak odbić dla wybranych filtrów.</div>}
          </div>
        </>
      )}
    </SectionCard>
  );
}

function DateRange({ from, to, setFrom, setTo }: { from: string; to: string; setFrom: (v: string) => void; setTo: (v: string) => void }) {
  return (
    <div className="flex flex-wrap items-end gap-2 text-xs">
      <label className="flex flex-col gap-1"><span className="section-label">Od</span><DatePicker value={from} onChange={setFrom} /></label>
      <label className="flex flex-col gap-1"><span className="section-label">Do</span><DatePicker value={to} onChange={setTo} /></label>
    </div>
  );
}

function ControlBoardTab() {
  const [rag, setRag] = useState("");
  const { data, isLoading } = useControlBoard(rag ? { rag } : {});
  const red = (data ?? []).filter((r) => r.rag === "red").length;
  return (
    <div className="mt-4 space-y-3">
      <div className="text-sm">
        {red > 0
          ? <span><Badge color="red">{red} {red === 1 ? "projekt wymaga interwencji" : "projekty wymagają interwencji"}</Badge> — mają pozycje po terminie. Szczegóły „co dokładnie” → zakładka TODO globalne.</span>
          : <span><Badge color="green">pod kontrolą</Badge> Żaden projekt nie ma pozycji po terminie.</span>}
      </div>
      <div className="flex flex-wrap gap-2 text-xs font-mono">
        {[["", "Wszystkie"], ["red", "Wymagają interwencji"], ["amber", "Blisko terminów"], ["green", "Pod kontrolą"]].map(([v, l]) => (
          <button key={v} onClick={() => setRag(v)}
            className={`px-2 py-1 border ${rag === v ? "border-[var(--color-primary)] text-[var(--color-primary)]" : "border-[var(--color-border)] text-[var(--color-text-muted)]"}`}>{l}</button>
        ))}
      </div>
      <SectionCard title={`Kondycja projektów (${data?.length ?? 0})`}>
        {isLoading ? <div className="text-sm text-[var(--color-text-muted)]">Ładowanie…</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-[var(--color-text-muted)] border-b border-[var(--color-border)]">
                  <th className="py-1.5 pr-2"><InfoDot text="Światło projektu: czerwone = coś jest po terminie, żółte = coś się zbliża do terminu, zielone = wszystko w terminach." /></th>
                  <th className="pr-2">Projekt</th><th className="pr-2">Prowadzi</th>
                  <th className="pr-2">Co wymaga uwagi<InfoDot text="Po terminie = limit czasu minął. Blisko terminu = zaraz minie. Bez ruchu = pozycja leży i nikt nic nie robi." /></th>
                  <th className="pr-2">Prognoza końca<InfoDot text="Przewidywana data zakończenia z symulacji procesu (ścieżka krytyczna). Strzałka = trend vs ~tydzień temu: ↗ czerwona = termin ucieka, ↘ zielona = się poprawia." /></th>
                  <th className="pr-2">Najdłużej wisi<InfoDot text="Najdłuższy czas, jaki pojedyncza pozycja czeka na czyjś ruch (nasz albo urzędu)." /></th>
                  <th className="pr-2">Ostatni ruch<InfoDot text="Ile dni temu cokolwiek wydarzyło się w projekcie. Czerwone = projekt wygląda na uśpiony." /></th>
                  <th className="pr-2">Termin umowy<InfoDot text="Ile dni zostało do umownego terminu zakończenia projektu." /></th>
                </tr>
              </thead>
              <tbody>
                {(data ?? []).map((r) => {
                  // „7 po terminie" znaczy co innego, gdy 6 z nich stoi w urzędzie —
                  // rozbicie idzie w tooltip i w dopisek przy odznace.
                  const breachSplit = r.breached > 0
                    ? [
                        r.breached_ours > 0 ? `${r.breached_ours} u nas` : null,
                        r.breached_office > 0 ? `${r.breached_office} w urzędzie` : null,
                      ].filter(Boolean).join(" / ")
                    : "";
                  const attention = [
                    r.breached > 0 && (
                      <Badge key="b" color="red">
                        <span title={breachSplit ? `Po terminie: ${breachSplit}` : undefined}>
                          {r.breached} po terminie
                          {breachSplit ? <span className="font-normal opacity-80"> ({breachSplit})</span> : null}
                        </span>
                      </Badge>
                    ),
                    r.warn > 0 && <Badge key="w" color="yellow">{r.warn} blisko terminu</Badge>,
                    r.stalled > 0 && <Badge key="s" color="gray">{r.stalled} bez ruchu</Badge>,
                  ].filter(Boolean);
                  return (
                    <tr key={r.connection_id} className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-elevated)]">
                      <td className="py-1.5 pr-2"><span className="inline-block w-3 h-3 rounded-full" style={{ background: RAG[r.rag] }} /></td>
                      <td className="pr-2 max-w-[300px]"><Link className="hover:text-[var(--color-primary)]" to={`/power-projects/${r.project_id}?tab=todo`}>{r.name}</Link></td>
                      <td className="pr-2 text-[var(--color-text-muted)]">{r.designer ?? "—"}</td>
                      <td className="pr-2"><span className="inline-flex flex-wrap gap-1">{attention.length ? attention : <span className="text-[var(--color-text-muted)]">✓ nic</span>}</span></td>
                      <td className="pr-2 whitespace-nowrap">
                        {r.completion_date ? (
                          <>
                            <span className="font-mono">{fmtDate(r.completion_date)}</span>
                            {r.forecast_flag === "will_miss" && <span className="ml-1"><Badge color="red">nie zdążysz</Badge></span>}
                            {r.forecast_flag === "at_risk" && <span className="ml-1"><Badge color="yellow">ryzyko</Badge></span>}
                            {r.trend_delta_days != null && r.trend_delta_days !== 0 && (
                              <span
                                className={`ml-1 text-xs font-mono font-bold ${r.trend_delta_days > 0 ? "text-red-700" : "text-green-700"}`}
                                title={r.trend_delta_days > 0
                                  ? `Termin UCIEKA: prognoza późniejsza o ${r.trend_delta_days} dni niż ~tydzień temu.`
                                  : `Prognoza poprawiła się o ${-r.trend_delta_days!} dni vs ~tydzień temu.`}
                              >
                                {r.trend_delta_days > 0 ? `↗ +${r.trend_delta_days}d` : `↘ ${r.trend_delta_days}d`}
                              </span>
                            )}
                          </>
                        ) : <span className="text-[var(--color-text-muted)]">—</span>}
                      </td>
                      <td className="pr-2 font-mono">{r.worst_court_days ? `${r.worst_court_days} dni` : "—"}</td>
                      <td className="pr-2">{r.dormant ? <Badge color="red">{r.dormancy_days} dni temu</Badge> : <span className="font-mono text-[var(--color-text-muted)]">{r.dormancy_days === 0 ? "dzisiaj" : `${r.dormancy_days} dni temu`}</span>}</td>
                      <td className="pr-2 font-mono">{r.days_to_deadline != null ? (r.days_to_deadline < 0 ? <span className="text-red-700">przekroczony o {-r.days_to_deadline} dni</span> : `za ${r.days_to_deadline} dni`) : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
}

// BEZ „kamienia": terminy etapów (kto co robi: my vs urząd) załatwiają FAZY kroków
// w szablonach procesu — nie reguły SLA. Stare reguły milestone zostają jako zapas.
const APPLIES = [
  ["proceeding", "Sprawa urzędowa"], ["owner_access", "Tytuł dostępu"],
  ["location", "Uzgodnienie lokalizacji (kategoria działki)"],
];

// Ludzkie wyjaśnienie per grupa: od czego liczy się zegar i co się dzieje po terminie
// (semantyka z backendu: process_control.ControlService / sla_of — nie zgadywana).
const APPLIES_HELP: Record<string, string> = {
  proceeding:
    "Każda OTWARTA sprawa w Korespondencji ma zegar liczony od ostatniego pisma w wątku — "
    + "obojętnie, czy piłka jest u nas, czy w urzędzie. Po tylu dniach bez ruchu pozycja robi się "
    + "czerwona w Kontroli i TODO projektu, a odpowiedzialny + dyrektorzy dostają poranny alert (06:45). "
    + "Klucz = typ sprawy. Ustawowy termin KPA to OSOBNY zegar — pilnowany zawsze, niezależnie od tej reguły.",
  location:
    "Wyjątki od zegarów lokalizacji z karty wyżej — reguła z konkretną kategorią (klucz) "
    + "na poziomie klienta/projektu/działki nadpisuje default firmowy (głębszy poziom wygrywa).",
  owner_access:
    "Zapas na przyszłość — ta reguła nie jest dziś podpięta pod żaden zegar (tytuły dostępu "
    + "pilnowane są przez statusy działek). Można ją zostawić bez zmian.",
};
// projektant/koordynator = jedno „My" — bez rozróżnienia (patrz RESPONSIBLE_ROLE_PL)
const ROLES = [["designer", "My"], ["office", "Urząd"], ["negotiator", "Negocjator"], ["investor", "Inwestor"]];

// Znane klucze per typ (poza kamieniami, które bierzemy z szablonów procesu).
const KEY_CHOICES: Record<string, [string, string][]> = {
  proceeding: [
    ["office", "Sprawa w urzędzie"], ["technical", "Uzgodnienie techniczne"],
    ["owner", "Zgoda właściciela (WPW)"], ["notification", "Zawiadomienie właścicieli"],
  ],
  parcel_status: [["action_required", "Wymaga działania"]],
  owner_access: [],
  location: [
    ["jst", "JST"], ["private_connected", "Prywatna — przyłączana"],
    ["private_transit", "Prywatna — tranzyt"], ["gddkia", "GDDKiA"], ["pkp", "PKP"],
    ["kowr", "KOWR"], ["lasy", "Lasy Państwowe"], ["wody", "Wody Polskie"], ["other", "Inne"],
    ["art124", "Art. 124"], ["uzupelnienie", "Uzupełnienie"], ["odwolanie", "Odwołanie"],
    ["zgloszenie_ze", "Zgłoszenie do ZE"], ["przeprojektowanie", "Przeprojektowanie"],
  ],
};

// Wiersz jednej reguły SLA — pełnym zdaniem: „X: alarm po N dniach, żółte M dni
// wcześniej, pilnuje: Y”, zamiast kryptonimów „21d ostrz. 5d”.
function SlaRuleChip({ r, onRemove }: { r: SlaRule; onRemove: () => void }) {
  return (
    <div className="flex items-center gap-2 border border-[var(--color-border)] rounded px-2 py-1.5 bg-[var(--color-bg-elevated)] text-xs">
      {r.scope !== "branch" && <Badge color="gray">{SLA_SCOPE_PL[r.scope] ?? r.scope}</Badge>}
      <span className="flex-1">
        <b>{slaKeyPl(r.applies_to, r.key)}</b>
        <span className="ml-1 font-mono text-[9px] text-[var(--color-text-muted)]">{r.key || "*"}</span>
        <span className="block text-[var(--color-text-muted)]">
          {r.applies_to === "location" ? (
            <>my <b className="text-[var(--color-text)]">{r.our_sla_days ?? "—"} dni</b> → oni{" "}
              <b className="text-[var(--color-text)]">{r.sla_days} dni</b>, żółte {r.warn_days} dni wcześniej</>
          ) : (
            <>alarm po <b className="text-[var(--color-text)]">{r.sla_days} dniach</b>, żółte
              ostrzeżenie {r.warn_days} dni wcześniej</>
          )}
        </span>
      </span>
      <Badge color={r.responsible_role === "negotiator" ? "yellow" : "gray"}>
        pilnuje: {RESPONSIBLE_ROLE_PL[r.responsible_role] ?? r.responsible_role}
      </Badge>
      <button className="text-[var(--color-primary)]" onClick={onRemove}>usuń</button>
    </div>
  );
}

function SlaEditorTab() {
  const { data: rows } = useSlaRules();
  const { data: templates } = useProcessTemplates();
  const m = useSlaMutation();
  const tplM = useProcessTemplateMutation();
  const [f, setF] = useState<Partial<SlaRule>>({ scope: "branch", branch: "energy", applies_to: "proceeding", key: "", sla_days: 30, warn_days: 5, responsible_role: "designer", is_active: true });
  // Edycja terminów z grafu: klik w blok → panel; zmiany trzymane w lokalnym drafcie
  // całego szablonu domyślnego aż do „Zapisz” (saveProcessTemplate).
  const [tplDraft, setTplDraft] = useState<ProcessTemplate | null>(null);
  const [selStepKey, setSelStepKey] = useState<string | null>(null);

  const keyChoices: [string, string][] = KEY_CHOICES[f.applies_to ?? ""] ?? [];
  const defaultTemplate = (templates ?? []).find((t) => t.is_default);
  const milestoneRules = (rows ?? []).filter((r) => r.applies_to === "milestone");
  const otherRules = (rows ?? []).filter((r) => r.applies_to !== "milestone");

  const shownTemplate = tplDraft ?? defaultTemplate;
  const selStep = selStepKey ? shownTemplate?.steps.find((s) => s.key === selStepKey) : undefined;
  const patchSelStep = (patch: Partial<ProcessTemplateStep>) => {
    if (!defaultTemplate || !selStepKey) return;
    const base = tplDraft ?? (JSON.parse(JSON.stringify(defaultTemplate)) as ProcessTemplate);
    setTplDraft({
      ...base,
      steps: base.steps.map((s) => (s.key === selStepKey ? { ...s, ...patch } : s)),
    });
  };

  return (
    <div className="mt-4 space-y-3">
      <SectionCard title="Kamienie procesu — sekwencje, terminy i zależności">
        <p className="text-xs text-[var(--color-text-muted)] mb-2">
          Każdy blok to kamień procesu podzielony na fazy: <b>1·MY</b> = nasz ruch ze swoim
          SLA, <b>2·URZĄD</b> = odpowiedź urzędu/instytucji ze swoim SLA — zegar zawsze biegnie
          stronie, która trzyma piłkę. Strzałki to zależności (co musi być wcześniej),
          pomarańczowa ścieżka = krytyczna. <b>Kliknij blok, żeby edytować jego terminy</b> —
          strukturę (dodawanie kroków, przeciąganie zależności) i szablony per klient
          zmienisz w zakładce „Szablony procesu”.
        </p>
        {shownTemplate ? (
          <MilestoneSequenceBoard
            template={shownTemplate}
            milestoneRules={milestoneRules}
            selectedKey={selStepKey}
            onStepClick={(k) => setSelStepKey((cur) => (cur === k ? null : k))}
          />
        ) : (
          <p className="text-xs text-[var(--color-text-muted)]">Ładowanie szablonu domyślnego…</p>
        )}

        {selStep && (
          <div className="mt-3 p-3 border border-[var(--color-border)] rounded bg-[var(--color-bg-elevated)]">
            <div className="section-label mb-2">Terminy kroku: {selStep.label || selStep.key}</div>
            <div className="flex flex-wrap items-end gap-2 text-sm">
              <label className="flex flex-col gap-1">
                <span className="section-label">Faza 1 — nasz ruch</span>
                <input className="input-base w-56" value={selStep.submit_label}
                  onChange={(e) => patchSelStep({ submit_label: e.target.value })} />
              </label>
              <label className="flex flex-col gap-1">
                <span className="section-label">SLA1 [dni]</span>
                <input type="number" className="input-base w-20" value={selStep.submit_sla_days}
                  onChange={(e) => patchSelStep({ submit_sla_days: Number(e.target.value) })} />
              </label>
              <label className="flex flex-col gap-1">
                <span className="section-label">Faza 2 — odpowiedź urzędu/instytucji</span>
                <input className="input-base w-56" value={selStep.resolve_label}
                  placeholder="puste = krok w całości po naszej stronie"
                  onChange={(e) => patchSelStep({ resolve_label: e.target.value })} />
              </label>
              <label className="flex flex-col gap-1">
                <span className="section-label">SLA2 [dni]</span>
                <input type="number" className="input-base w-20" value={selStep.resolve_sla_days}
                  onChange={(e) => patchSelStep({ resolve_sla_days: Number(e.target.value) })} />
              </label>
              <button type="button"
                className="inline-flex items-center text-xs font-mono uppercase tracking-wider px-3 py-1.5 bg-[var(--color-primary)] text-white disabled:opacity-50"
                disabled={!tplDraft || tplM.save.isPending}
                onClick={() => tplDraft && tplM.save.mutate(tplDraft, { onSuccess: () => setTplDraft(null) })}>
                {tplM.save.isPending ? "Zapisuję…" : "Zapisz"}
              </button>
              {tplDraft && (
                <button type="button" className="text-xs text-[var(--color-text-muted)]"
                  onClick={() => setTplDraft(null)}>anuluj zmiany</button>
              )}
              <button type="button" className="text-xs text-[var(--color-text-muted)] ml-auto"
                onClick={() => setSelStepKey(null)}>zamknij</button>
            </div>
            <p className="text-[10px] font-mono text-[var(--color-text-muted)] mt-2">
              Zapis zmienia szablon DOMYŚLNY — obowiązuje nowe projekty. Istniejące projekty
              zaktualizujesz propagacją w „Szablonach procesu”; per-projektowe wyjątki — w modalu
              kroku na grafie projektu.
            </p>
          </div>
        )}

        {milestoneRules.length > 0 && (
          <details className="mt-3">
            <summary className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-text-muted)] cursor-pointer">
              Terminy awaryjne kamieni ({milestoneRules.length}) — kiedy w ogóle działają?
            </summary>
            <p className="text-xs text-[var(--color-text-muted)] mt-2 mb-2">
              Normalnie termin kamienia wynika z jego faz na grafie powyżej. Jeśli jednak w jakimś
              projekcie krok <b>nie ma zdefiniowanych faz</b> (np. dodany ręcznie na grafie projektu
              albo projekt sprzed szablonów), system nie wie, ile krok ma trwać — wtedy bierze ten
              termin awaryjny (drobny szary napis „zapas bez faz” na bloku). Dla zwykłych projektów
              z szablonu te wartości są <b>nieużywane</b> — można je zostawić w spokoju.
            </p>
            <div className="grid gap-1.5 md:grid-cols-2 xl:grid-cols-3">
              {milestoneRules.map((r) => (
                <SlaRuleChip key={r.id} r={r} onRemove={() => r.id && m.remove.mutate(r.id)} />
              ))}
            </div>
          </details>
        )}
      </SectionCard>

      <SectionCard title="Zegary uzgodnień lokalizacji — per typ właściciela działki">
        <LocationClocksCard />
      </SectionCard>

      <SectionCard title="Zegary alarmów — kiedy coś robi się czerwone i idzie powiadomienie">
        <p className="text-xs text-[var(--color-text-muted)] mb-2">
          Graf wyżej ustawia terminy KROKÓW procesu. Tutaj są zegary dla wszystkiego, co żyje
          obok kroków: otwartych spraw w Korespondencji, działek czekających na nasz ruch
          i uzgodnień z właścicielami. Gdy zegar dobije do limitu, pozycja robi się
          <b> czerwona w zakładce Kontrola i w TODO projektu</b>, a odpowiedzialny + dyrektorzy
          dostają poranne powiadomienie (06:45). Wartości poniżej to domyślne dla całej firmy —
          wyjątek per klient / projekt / urząd / działka nadpisuje je (głębszy poziom wygrywa).
        </p>
        <div className="grid gap-4 md:grid-cols-2">
          {APPLIES.map(([value, label]) => {
            const group = otherRules.filter((r) => r.applies_to === value);
            return (
              <div key={value}>
                <div className="section-label mb-1">{label}</div>
                <p className="text-[11px] text-[var(--color-text-muted)] mb-1.5 leading-snug">
                  {APPLIES_HELP[value]}
                </p>
                {group.length ? (
                  <div className="space-y-1.5">
                    {group.map((r) => (
                      <SlaRuleChip key={r.id} r={r} onRemove={() => r.id && m.remove.mutate(r.id)} />
                    ))}
                  </div>
                ) : (
                  <p className="text-[10px] font-mono text-[var(--color-text-muted)]">brak reguł — dodaj poniżej</p>
                )}
              </div>
            );
          })}
        </div>
      </SectionCard>
      <SectionCard title="Dodaj zegar alarmu (domyślny dla całej firmy)">
        <div className="flex flex-wrap items-end gap-2 text-sm">
          <label className="flex flex-col gap-1"><span className="section-label">Czego pilnuje</span>
            <select className="input-base" value={f.applies_to} onChange={(e) => setF({ ...f, applies_to: e.target.value, key: "" })}>
              {APPLIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></label>
          <label className="flex flex-col gap-1"><span className="section-label">Który rodzaj</span>
            <select className="input-base w-56" value={f.key ?? ""} onChange={(e) => setF({ ...f, key: e.target.value })}
              disabled={keyChoices.length === 0}>
              <option value="">{keyChoices.length ? "wszystkie (domyślnie)" : "wszystkie (typ bez kluczy)"}</option>
              {keyChoices.map(([v, l]) => <option key={v} value={v}>{l} ({v})</option>)}
            </select></label>
          {f.applies_to === "location" && (
            <label className="flex flex-col gap-1"><span className="section-label">My [dni]</span>
              <input type="number" className="input-base w-24" value={f.our_sla_days ?? 5}
                onChange={(e) => setF({ ...f, our_sla_days: Number(e.target.value) })} /></label>
          )}
          <label className="flex flex-col gap-1">
            <span className="section-label">{f.applies_to === "location" ? "Oni [dni]" : "Alarm po [dniach]"}</span>
            <input type="number" className="input-base w-24" value={f.sla_days} onChange={(e) => setF({ ...f, sla_days: Number(e.target.value) })} /></label>
          <label className="flex flex-col gap-1"><span className="section-label">Żółte [dni wcześniej]</span>
            <input type="number" className="input-base w-24" value={f.warn_days} onChange={(e) => setF({ ...f, warn_days: Number(e.target.value) })} /></label>
          <label className="flex flex-col gap-1"><span className="section-label">Kto pilnuje</span>
            <select className="input-base" value={f.responsible_role} onChange={(e) => setF({ ...f, responsible_role: e.target.value })}>
              {ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></label>
          <button type="button" className="inline-flex items-center text-xs font-mono uppercase tracking-wider px-3 py-1.5 bg-[var(--color-primary)] text-white disabled:opacity-50"
            disabled={m.create.isPending}
            onClick={() => m.create.mutate({ ...f, scope: "branch", branch: "energy" })}>Dodaj</button>
        </div>
        <p className="text-[10px] font-mono text-[var(--color-text-muted)] mt-2">
          Nowa reguła z tym samym „czego pilnuje” + „który rodzaj” doprecyzowuje ogólną
          (konkretny rodzaj wygrywa z „wszystkie”). Terminy KROKÓW procesu ustawiasz na grafie
          wyżej, nie tutaj.
        </p>
      </SectionCard>
    </div>
  );
}

// ── Kalibracja SLA z historii (realne czasy faz vs szablon) ───────────────────
function CalibrationCard({ draft, onDraft }: { draft: ProcessTemplate; onDraft: (t: ProcessTemplate) => void }) {
  const { data } = useTemplateCalibration(draft.id);
  if (!draft.id || !data) return null;
  const rows = draft.steps.filter((s) => {
    const c = data.steps[s.key];
    return c && (c.submit_samples > 0 || c.resolve_samples > 0);
  });
  if (rows.length === 0) return null;
  const applyDays = (key: string, field: "submit_sla_days" | "resolve_sla_days", median: number | null) => {
    if (median == null) return;
    const days = Math.max(1, Math.round(median));
    onDraft({ ...draft, steps: draft.steps.map((s) => (s.key === key ? { ...s, [field]: days } : s)) });
  };
  return (
    <SectionCard title="Kalibracja z historii — ile te fazy trwają NAPRAWDĘ">
      <p className="text-xs text-[var(--color-text-muted)] mb-2">
        Mediany realnych czasów z historii zmian stanów kamieni
        ({data.scope === "client" ? "projekty tego klienta" : "wszystkie projekty"}).
        „Przyjmij” wpisuje medianę do szablonu (pamiętaj o „Zapisz szablon”).
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-[var(--color-text-muted)] border-b border-[var(--color-border)]">
              <th className="py-1.5 pr-2">Krok</th>
              <th className="pr-2">Faza 1 — SLA szablonu</th>
              <th className="pr-2">Faza 1 — realnie<InfoDot text="Mediana czasu od „w toku” do „wysłane” (nasza część kroku)." /></th>
              <th className="pr-2">Faza 2 — SLA szablonu</th>
              <th className="pr-2">Faza 2 — realnie<InfoDot text="Mediana czasu od „wysłane” do „gotowe” (odpowiedź strony zewnętrznej)." /></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => {
              const c = data.steps[s.key];
              const cell = (median: number | null, samples: number, field: "submit_sla_days" | "resolve_sla_days", current: number) => (
                samples === 0 ? <span className="text-[var(--color-text-muted)]">brak danych</span> : (
                  <span className={median != null && Math.round(median) > current ? "text-red-700" : ""}>
                    <b>{median} dni</b> <span className="text-[var(--color-text-muted)]">({samples} pr.)</span>
                    <button type="button" className="ml-1 text-xs text-[var(--color-primary)]"
                      onClick={() => applyDays(s.key, field, median)}>przyjmij</button>
                  </span>
                )
              );
              return (
                <tr key={s.key} className="border-b border-[var(--color-border)]">
                  <td className="py-1 pr-2"><b>{s.label}</b> <span className="font-mono text-[10px] text-[var(--color-text-muted)]">{s.key}</span></td>
                  <td className="pr-2 font-mono">{s.submit_sla_days} dni</td>
                  <td className="pr-2">{cell(c.submit_median_days, c.submit_samples, "submit_sla_days", s.submit_sla_days)}</td>
                  <td className="pr-2 font-mono">{s.resolve_sla_days} dni</td>
                  <td className="pr-2">{cell(c.resolve_median_days, c.resolve_samples, "resolve_sla_days", s.resolve_sla_days)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}

// ── Szablony procesu (metaszablon per klient + domyślny) ─────────────────────
function ProcessTemplatesTab() {
  const { data: templates } = useProcessTemplates();
  const { data: clients } = useClients();
  const m = useProcessTemplateMutation();
  const [selected, setSelected] = useState<string | "new" | null>(null);
  const [draft, setDraft] = useState<ProcessTemplate | null>(null);
  // propagacja zmian na istniejące projekty (opt-in + multiselect + podgląd dry-run)
  const [propagate, setPropagate] = useState(false);
  const [propIds, setPropIds] = useState<Set<string>>(new Set());
  const [applyMsg, setApplyMsg] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ templateId: string; rows: TemplateDiffRow[]; reset: boolean } | null>(null);
  // „akcje niebezpieczne" = hurtowy odpowiednik „Resetuj do szablonu" (kasuje kroki
  // spoza szablonu i ręczne zależności). Tylko dyrektor techniczny / CEO — backend
  // i tak wpuszcza na ten endpoint wyłącznie ich.
  const me = useAuthStore((st) => st.user);
  const canUnsafe = !!me?.is_ceo || (me?.role === "director" && !!me?.is_technical_director);
  const [unsafe, setUnsafe] = useState(false);
  const [targetQuery, setTargetQuery] = useState("");
  const { data: targetsData } = useTemplateTargets(draft?.id, propagate && !!draft);
  const targets = targetsData?.targets ?? [];
  // filtr tekstowy listy projektów — przyciski zaznaczania działają TYLKO na widocznych
  const visibleTargets = useMemo(() => {
    const q = targetQuery.trim().toLowerCase();
    if (!q) return targets;
    return targets.filter((t) => [t.name, t.client_name, t.designer]
      .some((v) => (v ?? "").toLowerCase().includes(q)));
  }, [targets, targetQuery]);
  const visibleIds = useMemo(() => new Set(visibleTargets.map((t) => t.connection_id)), [visibleTargets]);
  const hiddenSelected = [...propIds].filter((id) => !visibleIds.has(id)).length;
  // zaznaczenie zbiorcze operuje na widocznych: dokłada / zdejmuje tylko je
  const selectVisible = (rows: TemplateTarget[]) => {
    setPreview(null);
    setPropIds((prev) => {
      const next = new Set(prev);
      rows.forEach((t) => next.add(t.connection_id));
      return next;
    });
  };
  const deselectVisible = (rows: TemplateTarget[]) => {
    setPreview(null);
    setPropIds((prev) => {
      const next = new Set(prev);
      rows.forEach((t) => next.delete(t.connection_id));
      return next;
    });
  };

  // podpowiedzi kluczy: wbudowane kamienie (szablon domyślny) + klucze wszystkich szablonów
  const keySuggestions = useMemo(() => {
    const seen = new Map<string, string>();
    (templates ?? []).forEach((t) => t.steps.forEach((s) => { if (s.key && !seen.has(s.key)) seen.set(s.key, s.label); }));
    return [...seen.entries()].map(([key, label]) => ({ key, label }));
  }, [templates]);

  const pick = (t: ProcessTemplate | null, keepPropagation = false) => {
    if (!keepPropagation) {
      setPropagate(false);
      setPropIds(new Set());
      setUnsafe(false);
      setTargetQuery("");
    }
    setPreview(null);
    setApplyMsg(null);
    if (t === null) {
      setSelected("new");
      // nowy szablon startuje z kamieni szablonu domyślnego (endpoint listy gwarantuje jego istnienie)
      const def = (templates ?? []).find((x) => x.is_default);
      setDraft({
        client: null,
        name: "",
        steps: def ? (JSON.parse(JSON.stringify(def.steps)) as ProcessTemplate["steps"]) : [],
        dependencies: def ? (JSON.parse(JSON.stringify(def.dependencies)) as ProcessTemplate["dependencies"]) : [],
      });
    } else {
      setSelected(t.id ?? null);
      // głęboka kopia do edycji lokalnej
      setDraft(JSON.parse(JSON.stringify(t)) as ProcessTemplate);
    }
  };

  const stepKeys = (draft?.steps ?? []).map((s) => s.key).filter(Boolean);
  const hasDupKeys = new Set(stepKeys).size !== stepKeys.length;
  const clientsInUse = new Set((templates ?? []).filter((t) => t.id !== draft?.id).map((t) => t.client).filter(Boolean));

  return (
    <div className="mt-4 space-y-3">
      <SectionCard title="Szablony procesu — po jednym na klienta + domyślny">
        <p className="text-xs text-[var(--color-text-muted)] mb-2">
          Nowy projekt ZE dostaje kroki, fazy, SLA i zależności z szablonu swojego klienta
          (a gdy klient nie ma własnego — z domyślnego). Istniejący projekt można zresetować
          do szablonu przyciskiem „Resetuj do szablonu” pod grafem procesu.
        </p>
        <div className="flex flex-wrap gap-2 text-xs font-mono">
          {(templates ?? []).map((t) => (
            <button key={t.id} onClick={() => pick(t)}
              className={`px-2 py-1 border ${selected === t.id ? "border-[var(--color-primary)] text-[var(--color-primary)]" : "border-[var(--color-border)] text-[var(--color-text-muted)]"}`}>
              {t.name}{t.is_default ? " ★" : ""}
            </button>
          ))}
          <button onClick={() => pick(null)}
            className={`px-2 py-1 border ${selected === "new" ? "border-[var(--color-primary)] text-[var(--color-primary)]" : "border-dashed border-[var(--color-border)] text-[var(--color-text-muted)]"}`}>
            + nowy szablon
          </button>
        </div>
      </SectionCard>

      {draft && (
        <SectionCard title={draft.id ? `Edycja: ${draft.name}` : "Nowy szablon"}>
          <div className="flex flex-wrap items-end gap-2 text-sm mb-3">
            <label className="flex flex-col gap-1"><span className="section-label">Nazwa</span>
              <input className="input-base w-64" value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></label>
            <label className="flex flex-col gap-1"><span className="section-label">Klient</span>
              <select className="input-base" value={draft.client ?? ""} disabled={!!draft.is_default}
                onChange={(e) => setDraft({ ...draft, client: e.target.value || null })}>
                <option value="">— domyślny (bez klienta) —</option>
                {(clients ?? []).map((c) => (
                  <option key={c.id} value={c.id} disabled={clientsInUse.has(c.id)}>
                    {c.name}{clientsInUse.has(c.id) ? " (ma już szablon)" : ""}
                  </option>
                ))}
              </select></label>
          </div>

          <TemplateGraphEditor draft={draft} keySuggestions={keySuggestions}
            onDraft={(d) => { setPreview(null); setDraft(d); }} />

          <div className="mt-3">
            <CalibrationCard draft={draft} onDraft={(d) => { setPreview(null); setDraft(d); }} />
          </div>

          <div className="mt-4 pt-3 border-t border-[var(--color-border)]">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={propagate} onChange={(e) => { setPropagate(e.target.checked); setApplyMsg(null); }} />
              Zastosuj zmiany także do istniejących projektów
              <InfoDot text="Domyślnie tryb bezpieczny (jak przy tworzeniu projektu): w wybranych projektach dołoży brakujące kamienie, zaktualizuje nazwy/fazy/SLA po kluczu i dołoży zależności z szablonu. NICZEGO nie usuwa — stany kamieni, kroki ręczne i ręczne zależności zostają. Pełne wyrównanie struktury (z usuwaniem) = osobny przełącznik „akcje niebezpieczne” poniżej — dla dyrektora technicznego." />
            </label>
            {propagate && canUnsafe && (
              <label className="flex items-center gap-2 text-sm mt-2 ml-6">
                <input type="checkbox" checked={unsafe}
                  onChange={(e) => { setUnsafe(e.target.checked); setPreview(null); setApplyMsg(null); }} />
                <span className={unsafe ? "text-[var(--color-primary)] font-semibold" : ""}>
                  Pozwól na akcje niebezpieczne (pełne wyrównanie do szablonu)
                </span>
                <InfoDot text="To samo, co „Resetuj do szablonu” na stronie projektu, tylko hurtowo: kroki spoza szablonu (poza dodanymi ręcznie) zostaną USUNIĘTE, ręczne zależności skasowane, a układ kafelków wymieniony na szablonowy. Stany kroków o pasujących kluczach i kroki ręczne zostają. Podgląd przed zapisem pokaże dokładnie, co zniknie." />
              </label>
            )}
            {propagate && (
              <div className="mt-2">
                <input className="input-base w-full text-sm mb-1" value={targetQuery}
                  onChange={(e) => setTargetQuery(e.target.value)}
                  placeholder="Filtruj projekty (nazwa, klient, projektant) — przyciski poniżej działają na wyfiltrowanych" />
                <div className="flex flex-wrap gap-2 text-xs font-mono mb-1">
                  <button type="button" className="px-2 py-0.5 border border-[var(--color-border)] text-[var(--color-text-muted)]"
                    onClick={() => selectVisible(visibleTargets)}>zaznacz wszystkie ({visibleTargets.length})</button>
                  <button type="button" className="px-2 py-0.5 border border-[var(--color-border)] text-[var(--color-text-muted)]"
                    onClick={() => deselectVisible(visibleTargets)}>odznacz wszystkie</button>
                  {draft.client && (
                    <button type="button" className="px-2 py-0.5 border border-[var(--color-border)] text-[var(--color-text-muted)]"
                      onClick={() => selectVisible(visibleTargets.filter((t) => t.client_id === draft.client))}>
                      projekty tego klienta
                    </button>
                  )}
                  {draft.id && visibleTargets.some((t) => t.diverged) && (
                    <button type="button" className="px-2 py-0.5 border border-amber-600 text-amber-700"
                      onClick={() => selectVisible(visibleTargets.filter((t) => t.diverged))}>
                      rozjechane ({visibleTargets.filter((t) => t.diverged).length})
                    </button>
                  )}
                  <button type="button" className="px-2 py-0.5 border border-[var(--color-border)] text-[var(--color-text-muted)]"
                    onClick={() => { setPreview(null); setPropIds(new Set()); }}>wyczyść zaznaczenie</button>
                  <span className="self-center text-[var(--color-text-muted)]">
                    zaznaczone: {propIds.size}
                    {hiddenSelected > 0 && <span className="text-amber-700"> (w tym {hiddenSelected} poza filtrem)</span>}
                  </span>
                </div>
                <div className="max-h-56 overflow-y-auto border border-[var(--color-border)] rounded divide-y divide-[var(--color-border)]">
                  {targets.length === 0 && <div className="p-2 text-xs text-[var(--color-text-muted)]">Ładowanie projektów…</div>}
                  {targets.length > 0 && visibleTargets.length === 0 && (
                    <div className="p-2 text-xs text-[var(--color-text-muted)]">Filtr nie pasuje do żadnego projektu.</div>
                  )}
                  {visibleTargets.map((t) => (
                    <label key={t.connection_id} className="flex items-center gap-2 px-2 py-1 text-sm cursor-pointer hover:bg-[var(--color-bg-elevated)]">
                      <input type="checkbox" checked={propIds.has(t.connection_id)}
                        onChange={(e) => {
                          setPreview(null);
                          setPropIds((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(t.connection_id); else next.delete(t.connection_id);
                            return next;
                          });
                        }} />
                      <span className="flex-1 truncate">{t.name}</span>
                      <span className="text-xs text-[var(--color-text-muted)]">{t.designer ?? ""}</span>
                      {t.diverged === true && <Badge color="yellow">rozjechany</Badge>}
                      {t.client_id && t.client_id === draft.client && <Badge color="green">ten klient</Badge>}
                      {t.uses_template && <Badge color="gray">używa tego szablonu</Badge>}
                      {t.client_name && !(t.client_id === draft.client) && <span className="text-xs text-[var(--color-text-muted)]">{t.client_name}</span>}
                    </label>
                  ))}
                </div>
                {draft.id && (
                  <p className="text-[10px] font-mono text-[var(--color-text-muted)] mt-1">
                    „rozjechany” = struktura projektu różni się od OSTATNIO ZAPISANEGO szablonu (bieżące, niezapisane zmiany edytora nie są wliczone).
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 mt-4">
            <button
              className="inline-flex items-center text-xs font-mono uppercase tracking-wider px-3 py-1.5 bg-[var(--color-primary)] text-white disabled:opacity-50"
              disabled={m.save.isPending || m.apply.isPending || !draft.name.trim() || draft.steps.length === 0 || hasDupKeys
                || draft.steps.some((s) => !s.key.trim() || !s.label.trim())
                || draft.dependencies.some((d) => !d.node || !d.depends_on)
                || (propagate && propIds.size === 0)}
              onClick={() => m.save.mutate(
                { ...draft, steps: draft.steps.map((s, i) => ({ ...s, order: i })) },
                {
                  onSuccess: (saved) => {
                    const ids = [...propIds];
                    const wantPreview = propagate && ids.length > 0 && !!saved.id;
                    pick(saved, wantPreview);
                    if (wantPreview) {
                      // dry-run PO zapisie — diff liczony z aktualnej wersji szablonu
                      m.preview.mutate({ id: saved.id!, connections: ids, reset: unsafe }, {
                        onSuccess: (r) => setPreview({ templateId: saved.id!, rows: r.projects, reset: unsafe }),
                      });
                    }
                  },
                },
              )}
            >
              {propagate && propIds.size > 0 ? `Zapisz i pokaż podgląd (${propIds.size})` : "Zapisz szablon"}
            </button>
            {draft.id && (
              <button className="text-xs text-[var(--color-primary)]"
                title="Otwiera nowy szablon z kopią kroków i zależności (łącznie z niezapisanymi zmianami) — wskaż klienta i zapisz"
                onClick={() => {
                  const copy = JSON.parse(JSON.stringify({ steps: draft.steps, dependencies: draft.dependencies })) as Pick<ProcessTemplate, "steps" | "dependencies">;
                  pick(null);
                  setDraft({ client: null, name: `${draft.name} (kopia)`, ...copy });
                }}>
                utwórz kopię
              </button>
            )}
            {draft.id && !draft.is_default && (
              <button className="text-xs text-[var(--color-primary)]"
                disabled={m.remove.isPending}
                onClick={() => {
                  if (window.confirm(`Usunąć szablon „${draft.name}”? Projekty, które z niego korzystały, zachowają swoją strukturę.`)) {
                    m.remove.mutate(draft.id!, { onSuccess: () => { setSelected(null); setDraft(null); } });
                  }
                }}>
                usuń szablon
              </button>
            )}
            {m.save.isError && <span className="text-xs text-[var(--color-primary)]">Nie zapisano — sprawdź unikalność kluczy/klienta i czy zależności nie tworzą cyklu.</span>}
            {m.preview.isPending && <span className="text-xs text-[var(--color-text-muted)]">Szablon zapisany, liczę podgląd zmian…</span>}
            {m.preview.isError && <span className="text-xs text-[var(--color-primary)]">Szablon zapisany, ale podgląd się nie powiódł — zaznacz projekty i spróbuj ponownie.</span>}
            {applyMsg && <span className="text-xs text-green-700">{applyMsg}</span>}
          </div>

          {preview && (
            <TemplateDiffPreview
              rows={preview.rows}
              reset={preview.reset}
              pending={m.apply.isPending}
              error={m.apply.isError}
              onCancel={() => setPreview(null)}
              onApply={() => {
                const ids = preview.rows.map((r) => r.connection_id);
                if (preview.reset && !window.confirm(
                  `Wyrównać ${ids.length} ${ids.length === 1 ? "projekt" : "projektów"} do szablonu „${draft.name}" w trybie NIEBEZPIECZNYM?\n\n`
                  + "Kroki spoza szablonu (poza dodanymi ręcznie) zostaną usunięte, ręczne zależności skasowane, "
                  + "a układ kafelków wymieniony na szablonowy. Tej operacji nie da się cofnąć.",
                )) return;
                m.apply.mutate(
                  { id: preview.templateId, connections: ids, reset: preview.reset },
                  {
                    onSuccess: (r) => {
                      const done = preview.reset;
                      setPreview(null);
                      setPropagate(false);
                      setPropIds(new Set());
                      setUnsafe(false);
                      setApplyMsg(`Zastosowano do ${r.applied} ${r.applied === 1 ? "projektu" : "projektów"}`
                        + (done ? " — z pełnym wyrównaniem do szablonu." : " — bez usuwania czegokolwiek."));
                    },
                  },
                );
              }}
            />
          )}
        </SectionCard>
      )}
    </div>
  );
}

// ── podgląd dry-run propagacji: co dokładnie zmieni się w każdym projekcie ───
const DIFF_FIELD_PL: Record<string, string> = {
  label: "nazwa", submit_label: "faza 1", resolve_label: "faza 2",
  submit_sla_days: "SLA1", resolve_sla_days: "SLA2",
};
const fmtDiffVal = (v: string | number | null) => (v === null || v === "" ? "—" : String(v));

const rowDirty = (r: TemplateDiffRow) =>
  !!(r.add.length || r.update.length || r.edges_add.length
    || r.remove?.length || r.edges_remove?.length);

function TemplateDiffPreview({ rows, reset, pending, error, onApply, onCancel }: {
  rows: TemplateDiffRow[];
  reset: boolean;
  pending: boolean;
  error: boolean;
  onApply: () => void;
  onCancel: () => void;
}) {
  const changed = rows.filter(rowDirty);
  const removals = rows.reduce((n, r) => n + (r.remove?.length ?? 0), 0);
  const edgeRemovals = rows.reduce((n, r) => n + (r.edges_remove?.length ?? 0), 0);
  return (
    <div className={`mt-3 p-3 border rounded bg-[var(--color-bg-elevated)] ${reset ? "border-[var(--color-primary)]" : "border-[var(--color-border)]"}`}>
      <div className="section-label mb-1">
        Podgląd zmian — nic jeszcze nie zostało zastosowane
        {reset && <span className="ml-2 text-[var(--color-primary)]">tryb niebezpieczny (pełne wyrównanie)</span>}
      </div>
      <p className="text-xs text-[var(--color-text-muted)] mb-2">
        {changed.length === 0
          ? "Wszystkie zaznaczone projekty są już zgodne z szablonem — nie ma czego stosować."
          : <>Zmiany obejmą <b>{changed.length}</b> z {rows.length} zaznaczonych projektów.{" "}
            {reset
              ? <>Do usunięcia: <b>{removals}</b> {removals === 1 ? "kamień" : "kamieni"} spoza szablonu i <b>{edgeRemovals}</b> ręcznych zależności. Stany kamieni o pasujących kluczach i kroki dodane ręcznie zostają.</>
              : <>Niczego nie usuwamy — stany kamieni, kroki ręczne i ręczne zależności zostają.</>}
          </>}
      </p>
      <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
        {rows.map((row) => {
          const clean = !rowDirty(row);
          return (
            <div key={row.connection_id} className="text-sm">
              <span className="font-semibold">{row.name}</span>
              {clean ? (
                <span className="text-xs text-[var(--color-text-muted)] ml-2">✓ zgodny z szablonem — bez zmian</span>
              ) : (
                <ul className="text-xs space-y-0.5 ml-3 mt-0.5">
                  {row.add.map((a) => (
                    <li key={`a-${a.key}`} className="text-green-700">+ nowy kamień: {a.label} <span className="font-mono">({a.key})</span></li>
                  ))}
                  {(row.remove ?? []).map((d) => (
                    <li key={`d-${d.key}`} className="text-red-700 font-semibold">− USUNIĘTY kamień: {d.label} <span className="font-mono">({d.key})</span></li>
                  ))}
                  {row.update.map((u) => (
                    <li key={`u-${u.key}`} className="text-amber-700">
                      ~ {u.label} <span className="font-mono">({u.key})</span>:{" "}
                      {u.changes.map((c) => `${DIFF_FIELD_PL[c.field] ?? c.field} ${fmtDiffVal(c.old)} → ${fmtDiffVal(c.new)}`).join(", ")}
                    </li>
                  ))}
                  {row.edges_add.map((e, i) => (
                    <li key={`e-${i}`} className="text-[var(--color-text-secondary)] font-mono">→ nowa zależność: {e.node} ← {e.depends_on}</li>
                  ))}
                  {(row.edges_remove ?? []).map((e, i) => (
                    <li key={`er-${i}`} className="text-red-700 font-mono">✕ usunięta zależność: {e.node} ← {e.depends_on}</li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-2 mt-3">
        <button
          className="inline-flex items-center text-xs font-mono uppercase tracking-wider px-3 py-1.5 bg-[var(--color-primary)] text-white disabled:opacity-50"
          disabled={pending || changed.length === 0}
          onClick={onApply}
        >
          {reset ? "Wyrównaj do szablonu" : "Zastosuj do"}{" "}
          {changed.length === 1 ? "1 projektu" : `${changed.length} projektów`}
        </button>
        <button className="text-xs text-[var(--color-text-muted)]" onClick={onCancel}>anuluj (szablon pozostaje zapisany)</button>
        {pending && <span className="text-xs text-[var(--color-text-muted)]">Stosowanie…</span>}
        {error && <span className="text-xs text-[var(--color-primary)]">Nie udało się zastosować — spróbuj ponownie.</span>}
      </div>
    </div>
  );
}

// ── Podwykonawcy techniczni (mapa relacji: projekty + koordynatorzy) ────────────
function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
}

const AVATAR_TONES = [
  "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
  "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300",
];

function SubcontractorCard({ row }: { row: TechnicalSubcontractorRow }) {
  return (
    <div className="card p-4 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-semibold text-[var(--color-text-primary)] leading-tight">{row.name}</h3>
        <div className="flex shrink-0 gap-1.5">
          <Badge color="blue">{row.project_count} {row.project_count === 1 ? "projekt" : "projekty"}</Badge>
          <Badge color={row.active_orders > 0 ? "green" : "gray"}>{row.active_orders} aktywne</Badge>
          {row.rating != null && (
            <span title={`Ocena podwykonawcy — liczba ocen: ${row.ratings_count}`}>
              <Badge color="yellow" className="normal-case">★ {row.rating.toFixed(2)}</Badge>
            </span>
          )}
        </div>
      </div>

      <div>
        <p className="section-label mb-1.5">Przedsięwzięcia</p>
        <ul className="space-y-1">
          {row.projects.map((p) => (
            <li key={p.project_id} className="flex items-center gap-2 text-sm">
              <Link
                to={`/power-projects/${p.project_id}`}
                className="min-w-0 truncate hover:text-[var(--color-primary)]"
                title={p.name}
              >
                {p.name}
              </Link>
              <span className="ml-auto shrink-0 font-mono text-[11px] text-[var(--color-text-muted)]">
                {p.active_order_count}/{p.order_count} zam.
              </span>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <p className="section-label mb-1.5">Koordynatorzy</p>
        {row.coordinators.length === 0 ? (
          <p className="text-xs text-[var(--color-text-muted)]">— brak przypisanych —</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {row.coordinators.map((c, i) => (
              <span
                key={c.id}
                className="inline-flex items-center gap-1.5 rounded-full bg-[var(--color-bg-elevated)] pl-0.5 pr-2 py-0.5 text-xs"
                title={c.name}
              >
                <span
                  className={`grid h-5 w-5 place-items-center rounded-full text-[9px] font-bold ${AVATAR_TONES[i % AVATAR_TONES.length]}`}
                >
                  {initials(c.name)}
                </span>
                <span className="truncate max-w-[140px]">{c.name}</span>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TechnicalSubcontractorsTab() {
  const { data, isLoading } = useTechnicalSubcontractors();
  if (isLoading) return <div className="text-sm text-[var(--color-text-muted)] mt-4">Ładowanie…</div>;
  const rows = data ?? [];
  if (rows.length === 0)
    return (
      <div className="text-sm text-[var(--color-text-muted)] mt-4">
        Brak podwykonawców technicznych z powiązanymi zamówieniami projektów.
      </div>
    );
  const totalProjects = rows.reduce((a, r) => a + r.project_count, 0);
  const totalActive = rows.reduce((a, r) => a + r.active_orders, 0);
  return (
    <div className="mt-4 space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <StatTile label="Podwykonawcy techniczni" value={rows.length} tone="blue" />
        <StatTile label="Powiązane przedsięwzięcia" value={totalProjects} />
        <StatTile label="Aktywne zamówienia" value={totalActive} tone="ok" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {rows.map((row) => (
          <SubcontractorCard key={row.id} row={row} />
        ))}
      </div>
    </div>
  );
}

export function AdvisoryOversightPage() {
  usePageTitle("Doradca AI — nadzór");
  const [tab, setTab] = useState<OTab>("overview");
  return (
    <div className="space-y-4">
      <PageHeader backTo="/power-projects" backLabel="Portfel projektów ZE" title={<>Doradca AI — nadzór<Dlc05Badge /></>} />
      <Tabs
        tabs={[
          { key: "overview", label: "Przegląd" },
          { key: "todo", label: "TODO globalne" },
          { key: "control", label: "Tablica kontrolna" },
          { key: "analytics", label: "Analityka" },
          { key: "compare", label: "Porównanie projektów" },
          { key: "coordinators", label: "Koordynatorzy" },
          { key: "subcontractors", label: "Podwykonawcy techniczni" },
          { key: "meta", label: "Meta-skuteczność" },
          { key: "sla", label: "Reguły SLA" },
          { key: "templates", label: "Szablony procesu" },
        ]}
        active={tab}
        onChange={(k) => setTab(k as OTab)}
      />
      {tab === "overview" && <OverviewTab />}
      {tab === "todo" && <GlobalTodoTab />}
      {tab === "control" && <ControlBoardTab />}
      {tab === "analytics" && <AnalyticsTab />}
      {tab === "compare" && <CompareTab />}
      {tab === "coordinators" && <CoordinatorsTab />}
      {tab === "subcontractors" && <TechnicalSubcontractorsTab />}
      {tab === "meta" && <MetaTab />}
      {tab === "sla" && <SlaEditorTab />}
      {tab === "templates" && <ProcessTemplatesTab />}
    </div>
  );
}
